
import Vue from 'vue';
import BaseButton from '@core/components/UI/BaseButton.vue';

function hideBackground(modal: HTMLElement): () => void {
  const elements: Array<{ element: HTMLElement; ariaHidden: string | null; inert: boolean }> = [];
  let currentElement: HTMLElement | null = modal;

  while (currentElement?.parentElement) {
    const parent: HTMLElement = currentElement.parentElement;

    Array.from(parent.children).forEach((sibling) => {
      if (sibling === currentElement || !(sibling instanceof HTMLElement)) {
        return;
      }

      elements.push({
        element: sibling,
        ariaHidden: sibling.getAttribute('aria-hidden'),
        inert: sibling.hasAttribute('inert'),
      });
      sibling.setAttribute('aria-hidden', 'true');
      sibling.setAttribute('inert', '');
    });

    if (parent === document.body) {
      break;
    }

    currentElement = parent;
  }

  return () => {
    elements.forEach(({ element, ariaHidden, inert }) => {
      if (ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', ariaHidden);
      }

      if (!inert) {
        element.removeAttribute('inert');
      }
    });
  };
}

interface Data {
  loaded: boolean;
  previouslyFocusedElement: HTMLElement | null;
  restoreBackground: (() => void) | null;
}

interface Props {
  enableCloseButton: boolean;
  ariaLabel?: string;
  ariaLabelledby?: string;
}

interface Methods {
  close: () => void;
  onEscapeKeyUp: (event: KeyboardEvent) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  focusModal: () => void;
  getFocusableElements: () => HTMLElement[];
  restoreFocus: () => void;
}

interface Computed {
  closeLabel: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseButton,
  },
  props: {
    /**
     * Display close button on the top right corner
     */
    enableCloseButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    ariaLabel: {
      type: String,
      required: false,
    },
    ariaLabelledby: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      loaded: false,
      previouslyFocusedElement: null,
      restoreBackground: null,
    };
  },
  computed: {
    closeLabel(): string {
      return this.$t('words.close') as string;
    },
  },
  mounted() {
    this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    this.loaded = true;
    window.addEventListener('keyup', this.onEscapeKeyUp);
    window.addEventListener('keydown', this.onKeyDown);
    this.$nextTick(() => {
      const modal = this.$refs.modal as HTMLElement | undefined;
      if (modal) {
        this.restoreBackground = hideBackground(modal);
      }
      this.focusModal();
    });
  },
  destroyed() {
    window.removeEventListener('keyup', this.onEscapeKeyUp);
    window.removeEventListener('keydown', this.onKeyDown);
    this.restoreBackground?.();
    this.restoreFocus();
  },
  methods: {
    close() {
      this.$emit('close', true);
    },
    onEscapeKeyUp(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
    onKeyDown(event) {
      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        (this.$refs.modal as HTMLElement | undefined)?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    focusModal() {
      const focusableElements = this.getFocusableElements();
      const closeButton = (this.$refs.closeButton as Vue | undefined)?.$el as HTMLElement | undefined;
      const elementToFocus = closeButton || focusableElements[0] || (this.$refs.modal as HTMLElement | undefined);

      elementToFocus?.focus();
    },
    getFocusableElements() {
      const modal = this.$refs.modal as HTMLElement | undefined;
      if (!modal) {
        return [];
      }

      return Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ) as HTMLElement[];
    },
    restoreFocus() {
      this.previouslyFocusedElement?.focus?.();
    },
  },
});
